// src/helpers/generateDoc.js
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import logo from "../assets/images/logoDoc.png"; // URL resolved by bundler
import { toDataURL } from "./utility";

// Wire VFS (CRA/Webpack-friendly)
pdfMake.vfs = window.pdfMake.vfs;

// ---- Brand palette (adjust to your theme)
const BRAND = {
    primary: "#F97316",      // orange-500
    primarySoft: "#FFF2E8",  // soft orange bg
    ink: "#111827",          // gray-900
    subtle: "#6B7280",       // gray-500/600
    line: "#E5E7EB",         // gray-200
    tableHeader: "#F5F5F5",  // gray-100
    zebra: "#FAFAFA",        // gray-50
};

// ---- Logo preloader (no top-level await, caches result)
let _logoDataUrl = null;
let _logoPromise = null;
function getLogoDataUrl() {
    if (_logoDataUrl) return Promise.resolve(_logoDataUrl);
    if (_logoPromise) return _logoPromise;
    _logoPromise = toDataURL(logo).then((d) => (_logoDataUrl = d)).catch(() => null);
    return _logoPromise;
}

export async function generateDoc(docType, data = {}) {
    const tMap = {
        devis: "DEVIS",
        bon_commande: "BON DE COMMANDE",
        bon_livraison: "BON DE LIVRAISON",
        facture: "FACTURE",
    };

    // ---------------- Helpers ----------------
    const fmtMoney = (n) => {
        const x = Number(n || 0);
        const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
        return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };
    const fmtQty = (n) => {
        const s = String(Number(n ?? 0));
        const fixed = Number(s).toFixed(2);
        return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
    };
    function numberToFrenchWords(n) {
        if (n === 0) return "zéro";
        const U = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize"];
        const D = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante", "quatre-vingt", "quatre-vingt"];
        const H = (x) => (x === 0 ? "" : x === 1 ? "cent" : U[x] + " cent" + (x > 1 ? "s" : ""));
        const under100 = (x) => {
            if (x < 17) return U[x];
            const d = Math.floor(x / 10), u = x % 10;
            if (d === 7 || d === 9) return D[d] + (u === 1 ? " et " : "-") + (u ? U[10 + u] : U[10]);
            if (d === 8 && u === 0) return "quatre-vingts";
            if (u === 1 && d >= 1 && d <= 6) return D[d] + " et un";
            return D[d] + (u ? "-" + U[u] : "");
        };
        const under1000 = (x) => {
            const c = Math.floor(x / 100), r = x % 100;
            if (!c) return under100(r);
            return r ? `${H(c)} ${under100(r)}` : H(c);
        };
        let out = "";
        const m = Math.floor(n / 1_000_000);
        const k = Math.floor((n % 1_000_000) / 1000);
        const r = n % 1000;
        if (m) out += (m === 1 ? "un million" : under1000(m) + " millions");
        if (k) out += (out ? " " : "") + (k === 1 ? "mille" : under1000(k) + " mille");
        if (r) out += (out ? " " : "") + under1000(r);
        return out;
    }
    const toFr = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        return isNaN(dt) ? String(d) : dt.toLocaleDateString("fr-MA");
    };
    const amountToWordsMAD = (n) => numberToFrenchWords(Math.round(n || 0)).toUpperCase() + " DIRHAMS";

    // ---- Seller mapper (extended)
    function mapSellerToDoc(s, includeBankFooter = true) {
        const lineIF_ICE_Pat = [
            s?.identificationFiscale ? `IF: ${s.identificationFiscale}` : null,
            s?.ice ? `ICE: ${s.ice}` : null,
            s?.patente ? `Patente: ${s.patente}` : null,
        ].filter(Boolean).join("   •   ") || null;

        const lineRC_CNSS_Capital = [
            s?.rc ? `RC: ${s.rc}` : null,
            s?.cnss ? `CNSS: ${s.cnss}` : null,
            s?.capital ? `Capital: ${s.capital}` : null,
        ].filter(Boolean).join("   •   ") || null;

        const lineContacts = [
            s?.phone1 ? `Tél: ${s.phone1}` : null,
            s?.phone2 ? `Tél 2: ${s.phone2}` : null,
            s?.email ? `Email: ${s.email}` : null,
            s?.website ? `Site: ${s.website}` : null,
        ].filter(Boolean).join("   •   ") || null;

        const lines = [
            s?.subtitle || null,
            lineContacts,
                ].filter(Boolean);

        const addressLines =
            s?.address || s?.headOffice || s?.city || s?.country
                ? [
                    ...(s?.address ? String(s.address).split(/\r?\n/).filter(Boolean) : []),
                    [s?.headOffice, s?.city, s?.country].filter(Boolean).join(" • "),
                ].filter(Boolean)
                : [];

        const bankFooter =
            includeBankFooter &&
                (s?.bankName || s?.RIB || s?.iban || s?.swift || s?.bankAddress || s?.bankAgency)
                ? [
                    "Coordonnées bancaires",
                    [s?.bankName ? `Banque: ${s.bankName}` : null, s?.bankAgency ? `Agence: ${s.bankAgency}` : null].filter(Boolean).join("   •   "),
                    [s?.RIB ? `RIB: ${s.RIB}` : null, s?.iban ? `IBAN: ${s.iban}` : null, s?.swift ? `SWIFT: ${s.swift}` : null].filter(Boolean).join("   •   "),
                    s?.bankAddress ? `Adresse banque: ${s.bankAddress}` : null,
                ].filter(Boolean)
                : null;

        const legalMentions = s?.commercialCourt ? [`Registre au Tribunal de commerce: ${s.commercialCourt}`] : [];

        return {
            sellerDoc: { name: s?.name || "", lines, addressLines },
            bankFooter: bankFooter || undefined,
            legalMentions,
            modelInvoice: s?.modelInvoice || undefined,
            social: {
                facebook: s?.facebook || null,
                instagram: s?.instagram || null,
                tiktok: s?.tiktok || null,
                whatsapp: s?.whatsapp || null,
            },
            qrICE: s?.ice || null,
        };
    }

    // ---- MatjarEPOS doc mapper ----
    function mapMatjarToDoc(doc, { showPrices, forceTVA, priceMode }) {

        const wantHT = (priceMode || "TTC").toUpperCase() === "HT";

        const items = (doc.lignes || []).map((l) => {
            const unitHT = Number(l.prixUnitaireHT ?? 0);
            const unitTTC = l.prixUnitaireTTC ?? 0;
            const totalHT = Number(l.montantHT);
            const totalTTC = Number(l.montantTTC);
            const sub = [l.articleCode, l.unite, l.remise ? `Remise: ${l.remise}` : null].filter(Boolean).join(" • ");
            return {
                label: l.articleNom || "",
                sublabel: sub || undefined,
                qty: Number(l?.quantiteLivree ?? l?.quantite ?? 0),
                rate: showPrices ? (wantHT ? unitHT : unitTTC) : 0,
                total: showPrices ? (wantHT ? totalHT : totalTTC) : 0,
                _meta: { unitHT, unitTTC, totalHT, totalTTC }, // keep for totals
            };
        });

        const buyer = {
            name: doc.clientNom || "",
            lines: [
                doc.clientAdresse,
                doc.clientIce ? `ICE: ${doc.clientIce}` : null,
                doc.clientTelephone1 ? `Tél: ${doc.clientTelephone1}` : null,
                doc.clientEmail ? `Email: ${doc.clientEmail}` : null,
            ].filter(Boolean),
        };

        const meta = {
            number: doc.numero || "",
            date: toFr(doc.dateOperation || doc.dateCreation),
            place: (doc.chantierNom && doc.chantierNom !== "Aucun") ? `Chantier : ${doc.chantierNom}` : undefined,
        };

        const remarks = [
            doc.remarques,
            doc.validite ? `Validité: ${doc.validite} jours` : null,
            doc.dateExpiration ? `Expire le: ${toFr(doc.dateExpiration)}` : null,
            doc.modeReglement ? `Mode de règlement: ${doc.modeReglement}` : null,
            doc.statut ? `Statut: ${doc.statut}` : null,
        ].filter(Boolean).join("\n");

        let tvaRate;
        if (typeof forceTVA === "number") tvaRate = forceTVA;
        else if (Number(doc.montantTotalHT) > 0) {
            const rate = (Number(doc.montantTotalTaxe || 0) / Number(doc.montantTotalHT)) * 100;
            tvaRate = Math.round(rate);
            if (!isFinite(tvaRate) || tvaRate < 0) tvaRate = undefined;
        }

        return { buyer, meta, items, remarks, tvaRate, wantHT };
    }

    // ---------------- Orchestration ----------------
    const {
        // Option A: Matjar inputs
        matjarDoc,
        sellerModel,

        // Option B: Pre-mapped inputs
        seller,
        buyer,
        meta,
        items,
        remarks,
        tvaRate,
        logo: overrideLogo, // if you want to pass a custom base64/logo
        priceMode = "TTC",
        // Options générales
        currency = "MAD",
        forceTVA,
        showPrices = docType !== "bon_livraison",
        open = true,
        download = false,
    } = data;

    // Map seller & extended info
    const mappedSeller = sellerModel ? mapSellerToDoc(sellerModel, true) : null;
    let _seller = seller || mappedSeller?.sellerDoc || { name: "", lines: [], addressLines: [] };
    const _sellerExt = mappedSeller || {};

    // Merge doc parts
    let docBuyer = buyer, docMeta = meta, docItems = items, docRemarks = remarks, docTva = tvaRate, wantHT = priceMode === "HT";

    if (matjarDoc) {
        const m = mapMatjarToDoc(matjarDoc, { showPrices, forceTVA, priceMode });
        docBuyer = m.buyer;
        docMeta = m.meta;
        docItems = m.items;
        docRemarks = mergeRemarks(remarks, m.remarks, _sellerExt.bankFooter);
        docTva = showPrices ? m.tvaRate : undefined; // hide TVA if no prices
        const wantHT = m.wantHT;

    } else {
        docRemarks = mergeRemarks(remarks, null, _sellerExt.bankFooter);
    }

    function mergeRemarks(base, extra, bank) {
        const arr = [];
        if (base) arr.push(base);
        if (extra) arr.push(extra);
        if (bank && bank.length) arr.push(bank.filter(Boolean).join("\n"));
        return arr.join("\n\n");
    }

    // Totaux
    const sum = (arr, f) => (arr || []).reduce((s, it) => s + (Number(f(it)) || 0), 0);

    const grandHT = showPrices ? sum(docItems, it => it._meta?.totalHT) : 0;
    const grandTVA = showPrices ? sum(docItems, it => it._meta?.totalTTC - it._meta?.totalHT) : 0;
    const grandTTC = showPrices ? sum(docItems, it => it._meta?.totalTTC) : 0;
    const amountText = amountToWordsMAD(wantHT ? (grandHT + grandTVA) : grandTTC); // always words in TTC

    // Load logo (base64)
    const logoDataUrl = overrideLogo || (await getLogoDataUrl());

    // Status chip (VALIDE, etc.)
    const statusText = matjarDoc?.statut;
    const statusChip = statusText
        ? {
            margin: [0, 8, 0, 0],
            table: {
                widths: ["*"],
                body: [[{ text: String(statusText).toUpperCase(), alignment: "right", color: BRAND.primary }]],
            },
            layout: "noBorders",
        }
        : null;

    // Table header & rows
    const headerRow = showPrices
        ? [
            { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Article & Description", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: `P.U. ${wantHT ? "HT" : "TTC"}`, alignment: "right", style: "th", width: 80, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: `Total ${wantHT ? "HT" : "TTC"}`, alignment: "right", style: "th", width: 90, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
        ]
        : [
            { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Article & Description", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Quantité", alignment: "center", style: "th", width: 90, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
        ];

    const lines = (docItems || []).map((it, i) =>
        showPrices
            ? [
                { text: String(i + 1), alignment: "center" },
                [
                    { text: it.label, bold: true, color: BRAND.ink },
                    it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
                ],
                { text: fmtQty(it.qty), alignment: "center" },
                { text: fmtMoney(it.rate), alignment: "right" },
                { text: fmtMoney(it.total), alignment: "right" },
            ]
            : [
                { text: String(i + 1), alignment: "center" },
                [
                    { text: it.label, bold: true, color: BRAND.ink },
                    it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
                ],
                { text: fmtQty(it.qty), alignment: "center" },
            ]
    );
    const widths = showPrices ? [22, "*", 70, 80, 90] : [22, "*", 90];

    // Seller info block (LEFT column)
    const sellerInfoLines = [
        ...(_seller?.lines || []).map((l) => ({ text: l, style: "sellerLine" })),
                ...(_seller?.addressLines || []).map((l) => ({ text: l, style: "sellerLine", margin: [0, 2, 0, 0] })),

    ].filter(Boolean);

    const totalsCard = !showPrices ? null
        : wantHT
            ? {
                columns: [
                    { width: "*", text: "" },
                    {
                        width: 320,
                        table: {
                            widths: ["*", 140],
                            body: [
                                [{ text: "Sous-total HT", alignment: "right", color: BRAND.subtle },
                                { text: fmtMoney(grandHT), alignment: "right", color: BRAND.ink }],
                                [{ text: `TVA ${docTva ?? ""}%`, alignment: "right", color: BRAND.subtle },
                                { text: fmtMoney(grandTVA), alignment: "right", color: BRAND.ink }],
                                [{ text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink }],
                            ],
                        },
                        layout: {
                            fillColor: () => BRAND.primarySoft, hLineWidth: () => 0, vLineWidth: () => 0,
                            paddingTop: () => 10, paddingBottom: () => 10, paddingLeft: () => 10, paddingRight: () => 10,
                        },
                        margin: [0, 14, 0, 8],
                    },
                ],
            }
            : {
                columns: [
                    { width: "*", text: "" },
                    {
                        width: 320,
                        table: {
                            widths: ["*", 140],
                            body: [
                                [{ text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink }],
                                // optional small line showing HT/TVA
                                [{ text: "Dont HT / TVA", alignment: "right", color: BRAND.subtle, fontSize: 9 },
                                { text: `${fmtMoney(grandHT)} / ${fmtMoney(grandTVA)}`, alignment: "right", color: BRAND.subtle, fontSize: 9 }],
                            ],
                        },
                        layout: {
                            fillColor: () => BRAND.primarySoft, hLineWidth: () => 0, vLineWidth: () => 0,
                            paddingTop: () => 10, paddingBottom: () => 10, paddingLeft: () => 10, paddingRight: () => 10,
                        },
                        margin: [0, 14, 0, 8],
                    },
                ],
            };

    // Build the two footer lines from sellerModel (or seller)
    function buildFooterLines(fromSeller) {
        const s = fromSeller || {};
        // line 1: address - phone1[-phone2] - email
        const address = s.address ? String(s.address).replace(/\r?\n/g, ", ").trim() : null;
        const phones = [s.phone1, s.phone2].filter(Boolean).join("-");
        const email = s.email || null;
        const line1 = [address, phones || null, email].filter(Boolean).join(" - ");

        // line 2: RC: x - Patente: y - IF: z - CNSS: w (only show what exists)
        const regs = [
            s.ice ? `ICE: ${s.ice}` : null,
            s.rc ? `RC: ${s.rc}` : null,
            s.patente ? `Patente: ${s.patente}` : null,
            s.identificationFiscale ? `IF: ${s.identificationFiscale}` : null,
            s.cnss ? `CNSS: ${s.cnss}` : null,
        ].filter(Boolean).join(" - ");

        return { line1, regs };
    }


    const sellerForFooter = sellerModel || seller || {};
    const { line1: footerLine1, regs: footerLine2 } = buildFooterLines(sellerForFooter);


    // pdfmake docDefinition
    const dd = {
        pageMargins: [10, 10, 10, 84],
        content: [
            // Header
            {
                columns: [
                    // LEFT: Logo + Seller
                    {
                        width: "60%",
                        stack: [
                            ...(logoDataUrl ? [{ image: logoDataUrl, width: 180, margin: [0, 0, 0, 0] }] : []),
                            ...sellerInfoLines, // (stays as you already build it)
                        ],
                    },

                    // RIGHT: Title + Number + Date + Buyer card
                    {
                        width: "40%",
                        stack: [
                            { text: (tMap[docType] || "DOCUMENT"), style: "docTitle", alignment: "right", color: BRAND.ink },
                            { text: `#${docMeta?.number || ""}`, alignment: "right", color: BRAND.subtle, margin: [0, 2, 0, 6] },

                            // thin accent bar
                            { margin: [0, 2, 0, 8], canvas: [{ type: "rect", x: 320, y: 0, w: 195, h: 3 }] },

                            // Date (compact, right)
                            {
                                table: {
                                    widths: ["*"],
                                    body: [[{
                                        text: `${docType === "devis" ? "Date du Devis" : docType === "bon_commande"  ? "Date de commande" :  docType === "facture" ? "Date de Facture" : docType === "bon_livraison" ? "Date du BL" : "Date"} : ${docMeta?.date || ""}`,
                                        alignment: "right", color: BRAND.ink,
                                    }]],
                                },
                                layout: "noBorders",
                                margin: [0, 0, 0, 6],
                            },

                            // BUYER CARD (right side)
                            {
                                table: {
                                    widths: ["*"],
                                    body: [
                                        [{ text: (docType === "bon_livraison" ? "Livrer à" : "Facturer à"), bold: true, margin: [0, 2, 0, 2] }],
                                        [{ text: docBuyer?.name || "", bold: true }],
                                        ...((docBuyer?.lines || []).map(l => [{ text: l }])),
                                    ],
                                },
                                layout: {
                                    hLineWidth: () => 0,
                                    vLineWidth: () => 0,
                                    paddingTop: () => 2,
                                    paddingBottom: () => 2,
                                    paddingLeft: () => 2,
                                    paddingRight: () => 2,
                                },
                                margin: [0, 4, 0, 0],
                            },

                            // optional status chip
                            statusChip,
                        ].filter(Boolean),
                    },
                ],
                columnGap: 14,
                margin: [0, 0, 0, 18],
            },

            // Optional chantier/vehicle
            (docMeta?.vehicle || docMeta?.place) ? {
                text: [
                    docMeta?.vehicle ? `VÉHICULE : ${docMeta.vehicle}` : "",
                    (docMeta?.vehicle && docMeta?.place) ? "   •   " : "",
                    docMeta?.place ? `${docMeta.place}` : "",
                ],
                margin: [0, 0, 0, 8],
                color: BRAND.subtle,
            } : null,

            // Items table
            {
                table: {
                    headerRows: 1,
                    widths,
                    body: [headerRow, ...lines],
                },
                layout: {
                    fillColor: (rowIndex) => (rowIndex === 0 ? BRAND.tableHeader : rowIndex % 2 === 0 ? null : BRAND.zebra),
                    hLineWidth: () => 0.6,
                    vLineWidth: () => 0,
                    hLineColor: () => BRAND.line,
                    paddingTop: () => 7,
                    paddingBottom: () => 7,
                },
                margin: [0, 6, 0, 0],
            },

            // Totals (card)
            totalsCard,

            // Bank card (if any)
            (_sellerExt.bankFooter && _sellerExt.bankFooter.length) ? {
                margin: [0, 10, 0, 6],
                table: {
                    widths: ["*"],
                    body: [
                        [{ text: _sellerExt.bankFooter[0], bold: true, color: BRAND.ink }],
                        ..._sellerExt.bankFooter.slice(1).map((l) => [{ text: l, color: BRAND.subtle }]),
                    ]
                },
                layout: {
                    fillColor: () => BRAND.primarySoft,
                    hLineWidth: () => 0,
                    vLineWidth: () => 0,
                    paddingTop: () => 10,
                    paddingBottom: () => 10,
                    paddingLeft: () => 10,
                    paddingRight: () => 10,
                }
            } : null,

            // Remarks + amount in words
            { text: "Remarques", bold: true, margin: [0, 8, 0, 2], color: BRAND.ink },
            { text: docRemarks || "—", margin: [0, 0, 0, 6], color: BRAND.subtle },

            showPrices ? {
                text: `Arrêté le présent ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}`,
                color: BRAND.subtle,
                fontSize: 9,
                margin: [0, 20, 0, 20]
            } : null,
        ].filter(Boolean),

        // Footer (simple, keep or replace with your previous one)
        footer: function (currentPage, pageCount) {
            return {
                margin: [40, 0, 40, 20],
                stack: [
                    // thin separator
                    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#000' }], margin: [0, 4, 0, 6] },

                    // centered company info (from sellerModel)
                    {
                        alignment: 'center',
                        stack: [
                            { text: footerLine1 || "", fontSize: 8, color: '#333' },
                            { text: footerLine2 || "", fontSize: 8, color: '#333', margin: [0, 2, 0, 0] },
                        ],
                    },

                    // (optional) page number, keep or remove as you wish
                    {
                        columns: [
                            { width: '*', text: '' },
                            { width: 'auto', alignment: 'right', text: `Page ${currentPage} / ${pageCount}`, fontSize: 8, color: '#888' },
                        ],
                        margin: [0, 4, 0, 0],
                    },
                ],
            };
        },


        styles: {
            docTitle: { fontSize: 20, bold: true },
            sellerName: { bold: true, fontSize: 12 },
            sellerSub: { bold: true, fontSize: 9 },
            sellerLine: { fontSize: 9 },
            th: { bold: true },
            footerLine: { fontSize: 8, color: "#666" },
            footerPage: { fontSize: 8, color: "#888" },
        },
        defaultStyle: { fontSize: 10, color: BRAND.ink },
    };

    const fileName = `${(tMap[docType] || "document")}_${docMeta?.number || ""}.pdf`;
    if (download) return pdfMake.createPdf(dd).download(fileName);
    if (open) return pdfMake.createPdf(dd).open();
    return dd;
}
